/**
 * The PALM AMI004 file name contract: the shape PALM sends us, and how the PHYSICAL_INVENTORY_ID is
 * read out of it. Used by willChange_import_import_PALM, which parses the identifier out of the
 * file the user selects and makes it the import title.
 *
 * The regex and the human-readable convention live together so the message shown when a file name
 * does not conform cannot drift from the pattern that rejected it.
 */

/**
 * The pattern, written the way the ticket and the code talk about it. Placeholder syntax, so it
 * belongs in logs and developer-facing text - never in a message shown to an operator.
 */
export const PALM_AMI004_FILE_NAME_CONVENTION =
    '${prefixCode}_AMI004_O_${PHYSICAL_INVENTORY_ID}_YYYYMMDD-HHMM[SS].csv';

/**
 * The same shape as something an operator can compare their own file against. A concrete example
 * beats a template: `${prefixCode}` means nothing to the person who picked the file.
 */
export const PALM_AMI004_FILE_NAME_EXAMPLE = 'DER_AMI004_O_DER-PI2026_709799_20251219-1028.csv';

// Anchored at both ends: `prefixCode` is one underscore-free segment, and the name ends with the
// timestamp and `.csv`. Without the leading anchor a hand-renamed file such as
// `backup_DoJ_AMI004_O_PI-1_20260825-101500.csv` would still be parsed, and without the extension
// a name the convention does not describe would be accepted. The identifier capture stays greedy
// between those anchors, so a PHYSICAL_INVENTORY_ID containing its own underscores - or anything
// else PALM chooses to put there, since they do not constrain its format - survives intact.
//
// The seconds are optional. #1317 quotes the convention as `YYYYMMDD-HHMMSS`, but the files PALM
// actually sends carry minutes only - e.g. `DER_AMI004_O_DER-PI2026_709799_20251219-1028.csv` -
// so requiring six digits rejected real inbound files. Both widths are accepted, and nothing
// between four and six, so the trailing segment still has to be a timestamp rather than any run
// of digits.
const AMI004_FILE_NAME = /^[^_]+_AMI004_O_(.+)_\d{8}-\d{4}(?:\d{2})?\.csv$/i;

/**
 * @param {string} [fileName]
 * @returns {string|undefined} the PHYSICAL_INVENTORY_ID, or undefined if the name does not conform
 */
export function getPhysicalInventoryIdFromFileName(fileName = '') {
    const [, physicalInventoryId] = AMI004_FILE_NAME.exec(fileName) || [];
    return physicalInventoryId;
}
